import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Task2 } from "@/types/tasks";

interface QuickTaskFormProps {
  columnId: string;
  columnTitle: string;
  onCreateTask: (taskData: Partial<Task2>) => void;
  onOpenFullForm: (taskData: Partial<Task2>) => void;
  onClose: () => void;
}

export function QuickTaskForm({ columnId, columnTitle, onCreateTask, onOpenFullForm, onClose }: QuickTaskFormProps) {
  const [requester, setRequester] = useState("Usuário Atual"); // Default user
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState<Date>();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const taskData: Partial<Task2> = {
      title: description || `Nova tarefa - ${columnTitle}`,
      description,
      status: columnId,
      assignees: [{ id: "current", name: requester }],
      endDate: dueDate,
      priority: "medium",
      projectId: "1", // Default project
      projectName: "24.126 – Leroy Merlin – Bauru", // Default project
      setor: "",
      tipo: "Execução",
      createdAt: new Date(),
      updatedAt: new Date(),
      comments: [],
      attachments: [],
      checklist: [],
      followers: []
    };

    onCreateTask(taskData);
    onClose();
  };

  const handleOpenFull = () => {
    const taskData: Partial<Task2> = {
      title: description || `Nova tarefa - ${columnTitle}`,
      description,
      status: columnId,
      assignees: [{ id: "current", name: requester }],
      endDate: dueDate,
      priority: "medium",
      projectId: "1",
      projectName: "24.126 – Leroy Merlin – Bauru",
      setor: "",
      tipo: "Execução",
      createdAt: new Date(),
      updatedAt: new Date(),
      comments: [],
      attachments: [],
      checklist: [],
      followers: []
    };

    onOpenFullForm(taskData);
    onClose();
  };

  return (
    <Card className="w-72 shadow-lg border">
      <CardContent className="p-3 space-y-3">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Solicitante</label>
            <Input
              value={requester}
              onChange={(e) => setRequester(e.target.value)}
              className="text-xs h-8"
              placeholder="Nome do solicitante"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Tarefa</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="text-xs min-h-[60px] resize-none"
              placeholder="Descreva a tarefa..."
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Prazo de conclusão</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal h-8 text-xs",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3 w-3" />
                  {dueDate ? format(dueDate, "dd/MM/yyyy") : "Selecionar data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate}
                  onSelect={(date) => {
                    setDueDate(date);
                    // Close the popover by triggering click on the trigger
                    if (date) {
                      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
                    }
                  }}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenFull}
              className="flex-shrink-0 p-2"
            >
              <Plus className="h-3 w-3" />
            </Button>
            <Button type="submit" size="sm" className="flex-1 text-xs">
              Criar Tarefa
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-xs"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}